import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useChangeTier } from '@/hooks/useReferrals';
import { TIER_CONFIG, type ReferralTier } from '@/types/referral.types';

interface ChangeTierModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  currentTier: ReferralTier;
}

const tiers: ReferralTier[] = ['bronze', 'prata', 'ouro', 'embaixador'];

export default function ChangeTierModal({
  isOpen,
  onClose,
  userId,
  currentTier,
}: ChangeTierModalProps) {
  const [selectedTier, setSelectedTier] = useState<ReferralTier>(currentTier);
  const [error, setError] = useState('');

  const changeMutation = useChangeTier();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (selectedTier === currentTier) {
      setError('Selecione um tier diferente do atual');
      return;
    }

    try {
      await changeMutation.mutateAsync({
        userId,
        input: { tier: selectedTier },
      });
      handleClose();
    } catch {
      setError('Erro ao mudar tier');
    }
  };

  const handleClose = () => {
    setSelectedTier(currentTier);
    setError('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Mudar Tier"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <p className="text-sm text-yellow-800 dark:text-yellow-300">
            Mudar o tier manualmente pode afetar os beneficios do usuario.
            Os beneficios serao atualizados automaticamente.
          </p>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Selecione o novo tier
          </label>
          <div className="grid grid-cols-2 gap-3">
            {tiers.map((tier) => {
              const config = TIER_CONFIG[tier];
              const isSelected = selectedTier === tier;
              const isCurrent = currentTier === tier;

              return (
                <button
                  key={tier}
                  type="button"
                  onClick={() => setSelectedTier(tier)}
                  className={`
                    p-4 rounded-lg border-2 text-left transition-all
                    ${isSelected
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }
                  `}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`font-semibold ${config.color}`}>
                      {config.label}
                    </span>
                    {isCurrent && (
                      <span className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded">
                        Atual
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {config.minReferrals}-{config.maxReferrals || '+'} indicacoes
                  </p>
                  <div className="mt-2 space-y-1">
                    {config.benefits.slice(0, 2).map((benefit, i) => (
                      <p key={i} className="text-xs text-gray-600 dark:text-gray-400">
                        • {benefit}
                      </p>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-500 dark:text-red-400">{error}</p>
        )}

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="ghost" type="button" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            isLoading={changeMutation.isPending}
            disabled={selectedTier === currentTier}
          >
            Confirmar Mudanca
          </Button>
        </div>
      </form>
    </Modal>
  );
}
